/**
 * Responsive Image src helpers
 *
 * `DynamicImage` (`@salesforce/retail-react-app/app/components/dynamic-image`) wires up
 * `srcSet`/`sizes` by expanding the *bracketed* portion of `src` once per breakpoint width (and
 * again at 2× for DPR) — see `responsive-image.js#getSrc`. The helper here builds that bracketed
 * src for a given image URL, taking into account:
 *
 *   1. **Provider differences** — the two live image sources at King Living are:
 *      - **Cylindo Content API** — resizes via `size=` (longest edge, max 4096). Confirmed by KL
 *        ([KINDW-247](https://amblique.atlassian.net/browse/KINDW-247?focusedCommentId=1336799)):
 *        `size=` **does** vary per candidate, so we emit a **real** responsive `srcset` — a
 *        `[&size={width}]` template that `DynamicImage` expands to `…&size=360 360w, …&size=720 720w`
 *        etc. `feature=`/`background=` select *which* render; every other param (`w/h/q/dpr/fit/
 *        sw/sm`) is ignored.
 *      - **PIM / Pimly** (`/mobify/proxy/pim-images/…`) — a plain MRT proxy to Salesforce file
 *        storage with **no transform service**, so **no** URL resize param works. We serve PIM URLs
 *        **as-is** (no template); CLS is handled by explicit `width`/`height` attributes on the
 *        `<img>` and `loading="lazy"` on below-fold images at the call site. (KL does not use
 *        Scene7 DIS — it is out of scope.)
 *   2. **Existing query strings** — Cylindo URLs commonly arrive with `?background=FFFFFF` or
 *      `?feature=KEY:VALUE`. Naively appending `?size=…` produces `?…?size=…` (two `?`), which
 *      breaks the request. We detect the existing `?` and switch to `&` as the joiner.
 *
 * For Cylindo, the bare `src` attribute is the un-sized base URL (DynamicImage strips the `[…]`);
 * modern browsers always resolve from `srcSet`, so the size-less `src` is only a fallback for
 * pre-`srcset` browsers.
 *
 * @link https://amblique.atlassian.net/wiki/spaces/KL/pages/97091602/TSD+-+Core+Web+Vitals+and+Performance+Optimisations#Responsive-Image-Params
 */

const CYLINDO_HOST_RE = /(?:^|\.)cylindo\.com\b/i;

/**
 * Cheap host check that doesn't allocate a `URL` object on the hot path.
 * Accepts either an absolute URL or a path-relative one (returns `false` for the latter).
 * @param {string} url
 * @returns {boolean}
 */
const isCylindoUrl = (url) => CYLINDO_HOST_RE.test(url);

/**
 * Detect-and-undo percent-over-encoding (potentially multiple layers).
 *
 * Some King Living catalog data arrives already URL-encoded (e.g. `J2-PKG%209`), and a downstream
 * step then re-encodes the `%` itself, yielding `J2-PKG%25209` — Cylindo treats that as the
 * literal product code `J2-PKG%209`, fails to find it, and serves a 4xx (blank in the page).
 * Triple-encoding (`%2525209`) has been observed sporadically too, so we apply the rewrite in a
 * fixpoint loop rather than a single pass.
 *
 * Strategy: only rewrite the byte sequence `%25XX → %XX` when `XX` is two hex digits. That's
 * exclusively how `%XX` itself gets encoded, so the rewrite is unambiguous and leaves any other
 * legitimate percent-encodings untouched. We deliberately *don't* call `decodeURIComponent` on
 * the whole URL because that would over-decode reserved characters in the query string.
 *
 * The loop is bounded by string convergence — every iteration that doesn't shorten the string
 * exits — so it is O(depth-of-encoding) and safe on any well-formed URL.
 *
 * @param {string} url
 * @returns {string} URL with all detectable layers of `%25XX` over-encoding undone.
 */
const undoDoublePercentEncoding = (url) => {
  let prev = url;
  let next = url.replace(/%25([0-9A-Fa-f]{2})/g, '%$1');

  while (next !== prev) {
    prev = next;
    next = next.replace(/%25([0-9A-Fa-f]{2})/g, '%$1');
  }

  return next;
};

/**
 * Cylindo-only: rewrite over-encoded reserved characters that Cylindo's URL parser expects
 * literal in its query syntax.
 *
 * SCAPI productSearch normalises `imageGroups[].images[].link` more aggressively than
 * `getProduct` does, percent-encoding sub-delimiters like `:` even when the catalog stored
 * them literally. Cylindo's `feature=KEY:VALUE` syntax requires the literal `:` between the
 * key and value — `feature=UPHOLSTERY%3ABX-28` is rejected (or returns a default render
 * without the requested feature, which looks like the wrong product to the user).
 *
 * We deliberately scope this to `:` and to Cylindo URLs only. A blanket `decodeURIComponent`
 * would also decode characters that *must* stay encoded (`%20` for spaces in the product code
 * path, `%26` for an intentional literal `&` in a query value, etc.), corrupting the URL.
 *
 * If/when SCAPI starts encoding additional sub-delims that Cylindo rejects, extend this list
 * here rather than broadening the decode.
 *
 * @param {string} url - assumed to already be a Cylindo URL (caller guards via `isCylindoUrl`).
 * @returns {string}
 */
const decodeCylindoQuerySubDelims = (url) => url.replace(/%3A/gi, ':');

/**
 * Remove any `size=` query param from a Cylindo URL, preserving all other params (`feature=`,
 * `background=`, …) and a well-formed query string regardless of the param's position.
 *
 * Product-feed URLs frequently arrive with a large baked-in default (e.g. `&size=1250`). That is a
 * *resolution* hint only — `size=` never changes *which* render is served (that's `feature=` /
 * `background=`) — so for surfaces where we control sizing (responsive `srcset` or a right-sized
 * bucket) we strip the feed value and substitute our own rather than shipping a 1250px image into a
 * ~317px tile.
 *
 * @param {string} url
 * @returns {string}
 */
const stripCylindoSizeParam = (url) => {
  const qIdx = url.indexOf('?');

  if (qIdx === -1) return url;

  const path = url.slice(0, qIdx);
  const params = url
    .slice(qIdx + 1)
    .split('&')
    .filter((p) => p && !/^size=/i.test(p));

  return params.length ? `${path}?${params.join('&')}` : path;
};

/**
 * Per-surface Cylindo `size=` buckets — a fixed single-size value for surfaces that render a
 * **plain `<img>` / CSS `background-image`** (i.e. cannot use `DynamicImage`'s responsive `srcset`).
 *
 * ## Scope — plain surfaces only
 *
 * `DynamicImage` surfaces (product tiles, PDP hero slides, shop-the-look, in-stock carousels) get a
 * **real per-width `srcset`** from {@link buildDynamicImageSrc} (`[&size={width}]`, expanded from the
 * `widths` prop), so they do **not** use these buckets — the browser picks the right `size=` per
 * breakpoint/DPR automatically. These buckets apply to `normalizePlainTileImageSrc`
 * (cart/checkout/confirmation/account thumbnails, search typeahead) and swatch chips
 * ({@link buildSwatchSrc}), where we must commit to one width.
 *
 * ## Provider behaviour (verified 2026‑07; KL-confirmed, KINDW-247)
 *   - **Cylindo Content API** honours **only** `size=` for resizing (`w/h/q/dpr/fit/sw/sm` ignored).
 *     `size=` scales cleanly and *does* vary per value (200→~2.4 KB, 400→~5.7 KB, 800→~14 KB…).
 *   - **PIM / Pimly** supports **no** URL resize param — applying a bucket to a PIM URL is a
 *     harmless no-op (the helpers only append `size=` to Cylindo URLs). Right-sizing PIM is
 *     KL/Pimly-side.
 *
 * ## Sizing rule (plain surfaces)
 *
 * Each bucket ≈ **2–3× the CSS display width** of the surface, so a fixed single render stays crisp
 * on 2–3× DPR screens. `swatch` stays at the long-proven 80 (a 12–20px chip). `tile` (400) is the
 * default for `normalizePlainTileImageSrc`/GA4 fallbacks.
 */
export const CYLINDO_SIZE = Object.freeze({
  swatch: 80, //     12–20px chips (cover swatches)
  icon: 160, //      30–64px (account/config/radio thumbnails)
  thumb: 240, //     80–96px (cart flyout, checkout summary, cart line item)
  thumbLarge: 320, // 108–130px (typeahead, order confirmation)
  tile: 400, //      plain-img tile fallback / GA4 default
  hero: 1600, //     large plain-img fallback
});

/**
 * Default `size=` for plain Cylindo `<img>` URLs that don't already carry one (used by
 * {@link normalizePlainTileImageSrc} and {@link resolveAbsoluteImageUrl}). See {@link CYLINDO_SIZE}.
 */
const CYLINDO_DEFAULT_TILE_SIZE = CYLINDO_SIZE.tile;

/**
 * Apply the URL encoding fixes that `buildDynamicImageSrc` performs internally, but **without**
 * any `DynamicImage` template or `size=` injection. Suitable for any rendering path that needs a
 * directly-usable URL — CSS `background-image: url(...)`, Chakra `<Image src=…>` without
 * responsive `srcset`, `<link rel="preload">` href, `new Image()`, etc.
 *
 * Without this normalisation, swatches and other non-DynamicImage Cylindo URLs that arrive from
 * SCAPI productSearch with double-percent-encoded path segments (`%2520` instead of `%20`) hit
 * Cylindo with the broken path and 404 silently — there's no `onError` handler on a CSS
 * background-image to surface the failure.
 *
 * @param {string} url - Image URL from product data.
 * @returns {string} Normalised URL (or empty string for falsy input).
 */
export const normalizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';

  const normalized = undoDoublePercentEncoding(url);

  return isCylindoUrl(normalized) ?
    decodeCylindoQuerySubDelims(normalized) :
    normalized;
};

/**
 * Image URL for plain `<img>` / Chakra `<Image>` tiles (no `DynamicImage` `[…]` resize template).
 *
 * Reuses SCAPI productSearch encoding fixes from `normalizeImageUrl`, then forces a Cylindo
 * `size=` to the surface bucket (unsized Cylindo URLs can 404 or return unusable stubs in small
 * boxes). If the feed baked in its own `size=` (commonly a large default like `size=1250`), we
 * **replace** it with the bucket rather than keep it — a plain `<img>` can't carry a `srcset`, so
 * shipping a 1250px render into an 80px box is pure waste.
 *
 * Pass `cylindoSize` (from {@link CYLINDO_SIZE}) to right-size the render for the surface — e.g.
 * `CYLINDO_SIZE.thumb` for a cart line item instead of the full tile bucket. Non-Cylindo (PIM)
 * URLs are only encoding-normalised; PIM ignores every resize param, so the bucket is a harmless
 * no-op there.
 *
 * @param {string} url - Raw catalog URL (`disBaseLink` / `link`).
 * @param {Object} [opts]
 * @param {number} [opts.cylindoSize=CYLINDO_DEFAULT_TILE_SIZE] - Cylindo `size=` to apply.
 * @returns {string} Usable src, or empty string when input is invalid.
 */
export const normalizePlainTileImageSrc = (url, {cylindoSize = CYLINDO_DEFAULT_TILE_SIZE} = {}) => {
  if (!url || typeof url !== 'string') return '';

  const normalized = normalizeImageUrl(url);

  if (!normalized) return '';

  if (isCylindoUrl(normalized)) {
    if (/[?&]size=/i.test(normalized)) {
      return normalized.replace(/([?&]size=)\d+/i, `$1${cylindoSize}`);
    }
    const joiner = normalized.includes('?') ? '&' : '?';

    return `${normalized}${joiner}size=${cylindoSize}`;
  }

  return normalized;
};

const PIM_IMAGES_PROXY_SEGMENT = '/mobify/proxy/pim-images/';
const PIMLY_CDN_ORIGIN = 'https://pimly-prod-assets.pimlyapp.com';

/**
 * Resolve a catalog image URL to an absolute CDN URL for off-site consumers (GA4, email, etc.).
 *
 * PIM assets are stored as MRT proxy paths (`/mobify/proxy/pim-images/{pimImagePath}/…`) which
 * proxy to `pimly-prod-assets.pimlyapp.com` (see `config/default.js` `proxyConfigs`). Cylindo /
 * Scene7 URLs are already absolute but may need encoding normalisation and Cylindo `size=`.
 *
 * @param {string} url - Raw catalog URL (`disBaseLink` / `link`).
 * @returns {string} Absolute URL — empty string for falsy/non-string input.
 */
export const resolveAbsoluteImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';

  const trimmed = url.trim();

  if (!trimmed) return '';

  const proxyIdx = trimmed.indexOf(PIM_IMAGES_PROXY_SEGMENT);

  if (proxyIdx !== -1) {
    const assetPath = trimmed.slice(proxyIdx + PIM_IMAGES_PROXY_SEGMENT.length).replace(/^\/+/, '');

    return assetPath ? `${PIMLY_CDN_ORIGIN}/${assetPath}` : '';
  }

  return normalizePlainTileImageSrc(trimmed);
};

/**
 * Build a `DynamicImage`-compatible `src` that yields a **real** responsive `srcset`.
 *
 * Provider policy (see file header; confirmed by KL on
 * [KINDW-247](https://amblique.atlassian.net/browse/KINDW-247?focusedCommentId=1336799)):
 *
 *   - **Cylindo Content API**: return a `[&size={width}]` template. `DynamicImage` expands it into
 *     one `srcSet` candidate per breakpoint width (and 2× for DPR), e.g.
 *     `…&size=360 360w, …&size=720 720w, …&size=800 800w`. Because `size=` genuinely resizes
 *     (longest edge, max 4096), each candidate is a **different physical image** — real
 *     per-resolution download, so the browser fetches only what the slot needs. Any `size=` baked
 *     into the source URL by the feed (commonly a large default like `size=1250`) is **stripped**
 *     first — it's a resolution hint only, and keeping it would defeat the responsive `srcset`
 *     (every candidate would resolve to that one oversized image). `feature=`/`background=` are
 *     preserved.
 *   - **PIM / Pimly** (everything non-Cylindo — KL does not use Scene7): return the URL **as-is**
 *     (encoding-normalised, no template). PIM is a plain proxy with no transform service, so a
 *     resize param would be a no-op; adding one just yields a misleading `srcSet` of identical
 *     bytes. CLS/lazy are handled by explicit `width`/`height` + `loading` at the call site.
 *
 * @param {string} url - Base image URL (e.g. `imageGroups[].images[].disBaseLink`). Falsy returns ''.
 * @returns {string} src usable by `DynamicImage` — empty string when input is falsy.
 *
 * @example
 *   buildDynamicImageSrc('https://content.cylindo.com/api/v2/.../foo.webp?background=FFFFFF')
 *   // → 'https://…/foo.webp?background=FFFFFF[&size={width}]'
 *   //   → srcSet: '…&background=FFFFFF&size=360 360w, …&size=720 720w, …'
 *
 *   buildDynamicImageSrc('https://content.cylindo.com/api/v2/.../foo.webp?feature=X&size=1250')
 *   // → 'https://…/foo.webp?feature=X[&size={width}]'   (feed size stripped; per-width wins)
 *
 *   buildDynamicImageSrc('https://…/mobify/proxy/pim-images/…/jasper_1a')
 *   // → 'https://…/jasper_1a'   (PIM served as-is — no resize param works)
 */
export const buildDynamicImageSrc = (url) => {
  if (!url || typeof url !== 'string') return '';

  // Normalise potentially over-encoded source URLs **before** any further string manipulation —
  // otherwise the `[…]` template would be appended onto an already-broken base.
  const normalized = undoDoublePercentEncoding(url);

  if (isCylindoUrl(normalized)) {
    // SCAPI productSearch percent-encodes `:` to `%3A` in `feature=KEY:VALUE`; Cylindo rejects
    // the encoded form. Undo that single, well-known over-encoding for Cylindo URLs.
    const decoded = decodeCylindoQuerySubDelims(normalized);

    // Strip any feed-baked `size=` (e.g. `size=1250`) so it can't pin every srcset candidate to a
    // single oversized render; we own sizing here via the per-width template.
    const base = stripCylindoSizeParam(decoded);
    const cylindoJoiner = base.includes('?') ? '&' : '?';

    // Per-width template → real responsive srcset (see KINDW-247).
    return `${base}[${cylindoJoiner}size={width}]`;
  }

  // PIM (non-Cylindo): no transform service — serve as-is.
  return normalized;
};

/**
 * Target source pixel size for tile swatches.
 *
 * Tile swatches render at 20 × 20 CSS px (`SWATCH_DIAMETER` in the RangeProductTile theme), so a
 * source size of 80 keeps quality crisp at 4× DPR while staying well under 5 KB on Cylindo (and
 * a small handful of KB on Scene7) — vs. ~1 MB+ for the unsized Cylindo `hi-res` rendition the
 * catalog typically points at when no real swatch image is configured. Adjust here if the tile
 * design ever uses a larger swatch chip.
 */
export const SWATCH_TARGET_PX = CYLINDO_SIZE.swatch;

/**
 * Build a directly-usable image URL for a CSS `background-image: url(...)` swatch chip — i.e. a
 * normalised URL with a small fixed pixel size baked into the resize param.
 *
 * Why this exists alongside `buildDynamicImageSrc`:
 *   - `<DynamicImage>`'s `[…]` template syntax only works in `srcSet` (responsive image element).
 *     CSS background-image consumes a single string, so we can't defer width selection to the
 *     browser — we have to pick a width here.
 *   - `normalizeImageUrl` (encoding-only) was insufficient because the catalog often stores the
 *     same Cylindo URL under all view types (`hi-res`, `large`, `swatch`). Without an explicit
 *     `size=` Cylindo serves a full-resolution render — fine for a hero image, ~50× too much
 *     bandwidth for a 20-pixel circle.
 *
 * Provider behaviour:
 *   - **Cylindo**: append/replace `size={width}`. If the URL already declares a size (e.g. an
 *     ops override in BM), we *replace* it with the swatch-target value because the original
 *     was tuned for hero rendering.
 *   - **Scene7 / generic DIS**: append/replace `sw={width}` and `q=60`. Same replace-not-append
 *     reasoning.
 *
 * @param {string} url - swatch source URL (normalised or not).
 * @param {Object} [opts]
 * @param {number} [opts.width=SWATCH_TARGET_PX] - target pixel width to bake into the resize param.
 * @returns {string} Fully-qualified swatch URL — empty string for falsy/non-string input.
 */
export const buildSwatchSrc = (url, {width = SWATCH_TARGET_PX} = {}) => {
  if (!url || typeof url !== 'string') return '';

  const normalized = undoDoublePercentEncoding(url);

  if (isCylindoUrl(normalized)) {
    const decoded = decodeCylindoQuerySubDelims(normalized);

    if (/[?&]size=/i.test(decoded)) {
      return decoded.replace(/([?&]size=)\d+/i, `$1${width}`);
    }

    const joiner = decoded.includes('?') ? '&' : '?';

    return `${decoded}${joiner}size=${width}`;
  }

  if (/[?&]sw=/i.test(normalized)) {
    return normalized.replace(/([?&]sw=)\d+(?:\.\d+)?/i, `$1${width}`);
  }

  const joiner = normalized.includes('?') ? '&' : '?';

  return `${normalized}${joiner}sw=${width}&q=60`;
};

export default buildDynamicImageSrc;
